import React from 'react';
import { Container, Typography, Box, Fab, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Logout from '@mui/icons-material/Logout';
import AppsIcon from '@mui/icons-material/Apps';
import {Paper,  InputBase,  Divider} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import ContentPlayer from '../Player/ContentPlayer';
import { validateSearchQuery } from '../../utils/searchValidation';
import ContentSearchMessage, { searchContent, SEARCH_ERROR_MESSAGE, SEARCH_GAP_MESSAGE, GapComment, seekLabel } from '../common/ContentSearch';
import axios from 'axios';
import { readEmbedToken } from '../../utils/embedToken';
import { authService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { keyframes } from '@mui/system';
import { tokens } from '../../theme';
const pulse = keyframes`
  0% {
    transform: scale(.95);
    opacity: .5;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(.95);
    opacity: .5;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
`;

function ThinkingIndicator() {
  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${tokens.color.borderTertiary}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          background: tokens.color.bgPrimary
        }}
      >
        <AutoAwesomeIcon
          sx={{
            color: tokens.color.brand,
            fontSize: 28,
            animation: `${pulse} 1.5s infinite`
          }}
        />

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              mb: 1
            }}
          >
            Thinking...
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Searching your learning content and generating the best answer.
          </Typography>

          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                height: 10,
                borderRadius: 5,
                mb: 1,
                width: i === 3 ? '70%' : '100%',
                background:
                  `linear-gradient(90deg,${tokens.color.bgTertiary} 25%,${tokens.color.borderTertiary} 50%,${tokens.color.bgTertiary} 75%)`,
                backgroundSize: '400% 100%',
                animation: `${shimmer} 1.4s infinite linear`
              }}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

const EmbeddedDashboard = () => {
  const location = useLocation();
  // The widget's embed token (K13), read by the shared helper the /embed pages' workspace check uses too (K13 pt 17).
  // The widget's own search sends it; the server checks it.
  const embedToken = React.useMemo(() => readEmbedToken(), []);
  const { showToast } = useToast();
  const { openContentPlayer } = useContentPlayer();
  const [searchText, setSearchText] = React.useState('');
  const showNotification = (message, severity = 'success') => showToast(message, severity);
  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchText.trim()) {
      performSearch(searchText.trim());
    }
  };

  const [answer, setAnswer] = React.useState(null);
  const [queryId, setQueryId] = React.useState(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [feedbackGiven, setFeedbackGiven] = React.useState(null);
  const [fullAnswer, setFullAnswer] = React.useState(null);
  const [showFollowup, setShowFollowup] = React.useState(false);
  const [matchedContent, setMatchedContent] = React.useState(null);
  const [detailedView, setDetailedView] = React.useState(false);
  const [topMatches, setTopMatches] = React.useState([]);
  const [viewingRelated, setViewingRelated] = React.useState(false);
  const [relatedModules, setRelatedModules] = React.useState([]);
  const [relatedBatchIndex, setRelatedBatchIndex] = React.useState(0);
  const [isGap, setIsGap] = React.useState(false);
  // Skip on the related list ends the flow with "No worries. Search again anytime." (N4 C6).
  const [relatedSkipped, setRelatedSkipped] = React.useState(false);
  // The search the gap state belongs to, for "What were you looking for?" (N4 pt 4). The end of the related loop clears
  // queryId, so the gap keeps its own copy.
  const [gapQueryId, setGapQueryId] = React.useState(null);
  const [searchError, setSearchError] = React.useState(false);
  // A related step that failed (K15 pts 4-5): { step: 'related' } or { step: 'pick', module }. Shows the error
  // state under the conversation; Try again runs that step again.
  const [relatedFailure, setRelatedFailure] = React.useState(null);
  const [query, setQuery] = React.useState('');  
  const relatedBatchSize = 2;
  const getRelatedTotalBatches = () => Math.ceil((relatedModules?.length || 0) / relatedBatchSize);
  const hasMoreRelatedBatches = () => relatedBatchIndex + 1 < getRelatedTotalBatches();
  const showNextRelatedBatch = () => {
    if (hasMoreRelatedBatches()) {
      setRelatedBatchIndex(prev => prev + 1);
      return true;
    }
    return false;
  };
  // Secondary answer state (from clicking a related module)
  const [secondaryAnswer, setSecondaryAnswer] = React.useState(null);
  const [secondaryFullAnswer, setSecondaryFullAnswer] = React.useState(null);
  const [secondaryMatchedContent, setSecondaryMatchedContent] = React.useState(null);
  const [secondaryFeedbackGiven, setSecondaryFeedbackGiven] = React.useState(null);
  const [secondaryDetailedView, setSecondaryDetailedView] = React.useState(false);
  const [isViewingSecondaryAnswer, setIsViewingSecondaryAnswer] = React.useState(false);
  // A related pick runs the first answer's states (N4 pt 1): the loading line while its answer is made, and the
  // gap message when the item has no answer.
  const [relatedLoading, setRelatedLoading] = React.useState(false);
  const [secondaryIsGap, setSecondaryIsGap] = React.useState(false);

  const closePrimarySearchPlayer = () => {
    setDetailedView(false);
  };

    // gap message detection for styling
  const isGapMessage = (text) => {
    if (!text) return false;
    // Removed 10 Oct 2026 (N4 pt 2): matching 'Need help now? Contact the team', which no screen shows now.
    return /We don['’]t have content for this yet/.test(text);
  };
  // Check if answer indicates content doesn't cover the topic
  const isNoTopicCover = (text) => {
    return text && text.trim() === "The content does not cover this topic.";
  };
  async function performSearch(q) {
    if (!q.trim()) return;
    // ensure UI reflects chosen term when invoked with an argument
    if (q) setQuery(q);
    const validation = validateSearchQuery(q);
    if (!validation.isValid) {
        showNotification(validation.message, 'error');
        setQueryId(null);
        return;
    }
    setSearchLoading(true);
    setAnswer(null);
    setQueryId(null);
    setFullAnswer(null);
    setFeedbackGiven(null);
    setShowFollowup(false);
    setIsGap(false);
    setRelatedSkipped(false);
    setGapQueryId(null);
    setSearchError(false);
    setRelatedFailure(null);
    setRelatedModules([]);
    setRelatedBatchIndex(0);
    setViewingRelated(false);
    // A new search starts a new flow: no related answer or detailed view left over from the last one (N4 pt 1).
    setIsViewingSecondaryAnswer(false);
    setSecondaryIsGap(false);
    setDetailedView(false);

    try {
      // The embed search checks the widget's token (K13). Removed 11 Oct 2026 (K13): searching without it, which
      // the token check refused with 400 'Embed token is required' on every widget search.
      const outcome = await searchContent(q, {
        endpoint: '/api/public/embed/search',
        headers: embedToken ? { 'x-embed-token': embedToken } : {}
      });
      if (outcome.kind === 'validation') {
        showNotification(outcome.message, 'error');
        return;
      }
      // Signed out (K15 pt 11): show the widget's sign-in, keeping the embed parameters.
      if (outcome.kind === 'signin') {
        window.location.href = `/embed/login${location.search}`;
        return;
      }
      if (outcome.kind === 'error') {
        setSearchError(true);
        setAnswer(SEARCH_ERROR_MESSAGE);
        setFullAnswer(SEARCH_ERROR_MESSAGE);
        return;
      }
      const data = outcome.result;
      // API returns { answer, queryId, message, fullAnswer, summary }
      if (data) {
        const fullContent = data.fullAnswer || data.answer || data.message || '';
        const displayAnswer = data.answer || data.summary || summarizeText(fullContent, 4);
        const isGapResult = outcome.kind === 'gap';

        setIsGap(isGapResult);
        setGapQueryId(isGapResult ? (data.queryId || data.id || null) : null);
        setFullAnswer(fullContent);
        setAnswer(isGapResult ? SEARCH_GAP_MESSAGE : displayAnswer);
        setQueryId(data.queryId || data.id || null);
        setMatchedContent(data.matchedContent || null);
        setTopMatches(data.topMatches || []);
      }
    } catch (err) {
      console.error('Search error', err);
      setSearchError(true);
      setAnswer(SEARCH_ERROR_MESSAGE);
      setFullAnswer(SEARCH_ERROR_MESSAGE);
    } finally {
      setSearchLoading(false);
    }
  }

  function summarizeText(text, maxLines) {
    if (!text) return '';
    // split by newlines first, fallback to sentences
    const lines = text.split(/\n+/).map(l => l.trim()).filter(Boolean);
    if (lines.length >= maxLines) return lines.slice(0, maxLines).join('\n\n');
    // fallback to sentences
    const sentences = text.match(/[^.!?]+[.!?]?/g) || [text];
    return sentences.slice(0, Math.min(sentences.length, maxLines)).join(' ');
  }

  async function sendFeedback(intent, contentId = null, preserveUI = false) {
    if (!queryId) return;
    // prevent duplicate submission of the same intent, allow different intents to overwrite
    if (feedbackGiven && feedbackGiven === intent) return;
    // optimistic set to reflect user's choice
    setFeedbackGiven(intent);
    try {
      const resp = await fetch('/api/user-queries/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ queryId, intent, contentId })
      });
      if (!resp.ok) {
        // revert on failure
        setFeedbackGiven(null);
        console.error('Feedback failed', resp.statusText || resp.status);
      }
      else {
        if (intent === 'yes') {
          setShowFollowup(false);
        }
        if (intent === 'skip') {
          if (!preserveUI) {
            setAnswer(null);
            setFullAnswer(null);
            setQueryId(null);
            setMatchedContent(null);
            setTopMatches([]);
            setViewingRelated(false);
          }
        }
      }
    } catch (err) {
      console.error('Feedback error', err);
      setFeedbackGiven(null);
    }
  }

  // Fetch related modules from server when user requests refinement
  async function fetchRelatedModules() {
    if (!queryId && !query) return;
    setRelatedFailure(null);
    try {
      // The question that was searched (K20.1.a pt 5). Removed 10 Oct 2026 (N4 pt 1): sending the text box, which
      // the learner may have changed since.
      const body = { q: query };

      const resp = await fetch('/api/contents/related-modules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      if (resp.ok) {
        const data = await resp.json();
        const modules = data && data.modules ? data.modules : (data || []);
        const uniq = [];
        const seen = new Set();
        if (matchedContent && matchedContent.id) {
          seen.add(matchedContent.id);
        }
        for (const m of modules) {
          if (!m || m.id == null) continue;
          if (seen.has(m.id)) continue;
          seen.add(m.id);
          uniq.push(m);
        }
        // No related item left: the gap state, as in the app (N4 pt 2). Removed 10 Oct 2026 (N4 pt 2): showing the empty
        // list, which left the widget blank.
        if (!uniq.length) {
          setDetailedView(false);
          endInGapState();
          return;
        }
        setRelatedModules(uniq.slice(0, 6));
        setRelatedBatchIndex(0);
        setViewingRelated(true);
        setAnswer(null);
        setDetailedView(false);
      } else {
        // Removed 10 Oct 2026 (K15 pt 5): failing silently, which left the learner with nothing on screen.
        console.error('Failed to fetch related modules');
        setRelatedFailure({ step: 'related' });
      }
    } catch (err) {
      console.error('Error fetching related modules', err);
      setRelatedFailure({ step: 'related' });
    }
  }

  // Try again on the error state: run the related step that failed once more (K15 pt 4).
  function retryRelatedStep() {
    const failed = relatedFailure;
    setRelatedFailure(null);
    if (!failed) return;
    if (failed.step === 'pick') handleRelatedModuleClick(failed.module);
    else fetchRelatedModules();
  }

  // Resolve or update feedback after showing detailed answer
  async function resolveQuery(intent) {
    if (!queryId) return;
    try {
      const resp = await fetch('/api/user-queries/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ queryId, intent })
      });
      if (resp.ok) {
        setFeedbackGiven(intent);
        if (intent === 'yes') {
          setShowFollowup(false);
        } else if (intent === 'no') {
          fetchRelatedModules();
        }
      } else {
        console.error('Resolve failed', resp.statusText || resp.status);
      }
    } catch (err) {
      console.error('Resolve error', err);
    }
  }

  // Handle clicking on a related module
  async function handleRelatedModuleClick(module) {
    setRelatedFailure(null);
    setRelatedLoading(true);
    setViewingRelated(false);
    setIsViewingSecondaryAnswer(false);
    try {
      // 1. Mark as picked result
      if (queryId) {
        axios.post('/api/user-queries/pick', { queryId, pickedResult: module.id })
            .catch(err => console.warn('Failed to mark picked result:', err?.response?.data || err.message));
      }

      // 2. The player enrols the learner in the content's course when it starts playing (N40.1 pt 5).
      // Removed 10 Oct 2026 (N4 pt 1): calling enroll-on-content-access on the click, before anything played.

      // 3. Generate answer for this content
      const genResp = await axios.post(`/api/contents/${module.id}/generate-answer`, { query });
      const genData = genResp?.data;
      if (genResp.status === 200 && genData && genData.success) {
        // Set secondary answer state
        const fullContent = genData.answer || '';
        const displayAnswer = genData.shortanswer || summarizeText(fullContent, 4);
        const contentMetadata = genData.content || {};
        // The server sends the gap message with no content when the item has no answer (N4 pt 1).
        const noCoverage = !genData.content || isNoTopicCover(fullContent) || isNoTopicCover(displayAnswer);
        setSecondaryIsGap(noCoverage);

        setSecondaryMatchedContent(noCoverage ? null : {
          id: module.id,
          title: module.title || contentMetadata.name || contentMetadata.title || 'Content',
          type: module.type,
          courseId: module.courseId,
          courseName: module.courseName,
          duration: module.duration || contentMetadata.duration || null,
          videoUrl: module.url && module.type === 'video' ? module.url : null,
          // the page or time this answer cites, so the player opens there (K20.1.a pts 7 and 9)
          seek: genData.seek || null
        });
        setSecondaryAnswer(displayAnswer);
        setSecondaryFullAnswer(fullContent);
        setSecondaryFeedbackGiven(null);
        setSecondaryDetailedView(false);
        setIsViewingSecondaryAnswer(true);

        // Hide related modules view while the secondary answer is shown
        setViewingRelated(false);
      } else {
        // Removed 10 Oct 2026 (K15 pts 4-5): the 'Failed to load answer' and 'Error loading content' toasts. A failed
        // answer shows the error state in the conversation, like the first search.
        console.error('Failed to generate answer for module', genData || genResp);
        setViewingRelated(true);
        setRelatedFailure({ step: 'pick', module });
      }
    } catch (err) {
      console.error('Error handling module click', err);
      setViewingRelated(true);
      setRelatedFailure({ step: 'pick', module });
    } finally {
      setRelatedLoading(false);
    }
  }

  // After No on a related answer, or an item with no answer: the next related items, or the gap state once they have
  // run out (N4 pts 1-2). Removed 10 Oct 2026 (N4 pt 2): fetching the same list again when the items ran out, so the
  // loop never reached the gap state.
  function showNextRelatedOrGap() {
    setIsViewingSecondaryAnswer(false);
    setSecondaryIsGap(false);
    if (showNextRelatedBatch()) {
      setViewingRelated(true);
      return;
    }
    endInGapState();
  }

  // The end of the road (N4 pt 2): only the gap state, with its one optional question (N4 pt 4).
  function endInGapState() {
    setViewingRelated(false);
    setRelatedModules([]);
    setFullAnswer(null);
    setAnswer(null);
    setIsGap(true);
    setGapQueryId(queryId);
    setQueryId(null);
    setMatchedContent(null);
    setTopMatches([]);
  }

  // Send feedback for secondary answer
  async function sendSecondaryFeedback(intent, contentId = null, preserveUI = false) {
    if (!queryId) return;
    if (secondaryFeedbackGiven && secondaryFeedbackGiven === intent) return;
    
    setSecondaryFeedbackGiven(intent);
    try {
      const resp = await fetch('/api/user-queries/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ queryId, intent, contentId })
      });
      if (!resp.ok) {
        setSecondaryFeedbackGiven(null);
        console.error('Secondary feedback failed', resp.statusText || resp.status);
      } else {
        if (intent === 'yes') {
          setShowFollowup(false);
        }
        if (intent === 'skip') {
          if (!preserveUI) {
            setIsViewingSecondaryAnswer(false);
            setSecondaryAnswer(null);
            setSecondaryFullAnswer(null);
            setSecondaryMatchedContent(null);
            setSecondaryFeedbackGiven(null);
          }
        }
      }
    } catch (err) {
      console.error('Secondary feedback error', err);
      setSecondaryFeedbackGiven(null);
    }
  }

  // Resolve secondary answer
  async function resolveSecondaryQuery(intent) {
    if (!queryId) return;
    try {
      const resp = await fetch('/api/user-queries/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ queryId, intent })
      });
      if (resp.ok) {
        setSecondaryFeedbackGiven(intent);
        if (intent === 'yes') {
          setShowFollowup(false);
        } else if (intent === 'no') {
          setIsViewingSecondaryAnswer(false);
          setSecondaryAnswer(null);
          setSecondaryFullAnswer(null);
          setSecondaryMatchedContent(null);
          setSecondaryFeedbackGiven(null);
          setSecondaryDetailedView(false);
          showNextRelatedOrGap();
        }
      } else {
        console.error('Secondary resolve failed', resp.statusText || resp.status);
      }
    } catch (err) {
      console.error('Secondary resolve error', err);
    }
  }

  // Add logout handler
  const handleLogout = async () => {
    try {
      await authService.logout();
      
      // Clear local storage
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userData');
      localStorage.removeItem('embedContext');
      
      // Navigate to embed login with current URL parameters
      const currentParams = location.search;
      window.location.href = `/embed/login${currentParams}`;
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userData');
      localStorage.removeItem('embedContext');
      
      const currentParams = location.search;
      window.location.href = `/embed/login${currentParams}`;
    }
  };

  // ── SAFETY: content open + pick tracking + enroll-on-access (unchanged) ────
  const handleContentClick = async (contentId, courseId) => {
      if (queryId) {
          axios.post('/api/user-queries/pick', { queryId, pickedResult: contentId })
              .catch(err => console.warn('Failed to mark picked result:', err?.response?.data || err.message));
      }
      const seek = matchedContent?.seek || null;
      openContentPlayer(courseId || null, contentId, courseId ? `/courses/${courseId}` : '/', null, true, null, null, null, null, searchText, queryId, seek, true);
  };

  return (
    <>
      {/* Logout Button */}
      <Fab
        color="secondary"
        aria-label="logout"
        title="Logout"
        onClick={handleLogout}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: 'text.secondary',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }
        }}
      >
        <Logout />
      </Fab>

      <Container sx={{ py: 4 }}>
        {/* Main content - remove the centered title since it's now in header */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
          <Typography variant="h5" component="h2" color="text.secondary">
            Welcome to your learning dashboard
          </Typography>
        </Box>
        
        {/* Search Bar */}
        <Box sx={{  maxWidth: 900,  mx: 'auto',  mt: 4  }}
        >
          <Paper
            elevation={1}
            sx={{  borderRadius: 4,  overflow: 'hidden'
            }}
          >
            {/* Search Input */}
            <Box
              sx={{  display: 'flex',  alignItems: 'center',  px: 2,  py: 1  }}
            >
              <StarBorderIcon sx={{ color: tokens.color.textTertiary, mr: 1 }} />

              <InputBase
                placeholder="Ask anything about product..."
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleSearch}
              />

              <SearchIcon
                sx={{
                  color: tokens.color.textTertiary,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (searchText.trim()) {
                    performSearch(searchText.trim());
                  }
                }}
              />
            </Box>

            <Divider />
            {searchLoading && <ThinkingIndicator />}
            
            {/* AI Answer + Feedback or Gap */}
            {isGap && (
              <Box sx={{ p: 2 }}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backgroundColor: tokens.color.status.warnBg, border: `0.5px solid ${tokens.color.status.warnText}` }}>
                  <ContentSearchMessage kind="gap" sx={{ fontSize: '16px', color: tokens.color.status.warnText, whiteSpace: 'pre-line' }} />
                  {/* The one optional question at the gap state (N4 pt 4) */}
                  <GapComment queryId={gapQueryId} sx={{ color: tokens.color.status.warnText }} />
                </Paper>
              </Box>
            )}
            {searchError && (
              <Box sx={{ p: 2 }}>
                <ContentSearchMessage kind="error" onRetry={() => performSearch(query)} />
              </Box>
            )}
            {relatedSkipped && !answer && !isGap && !searchError && !viewingRelated && (
              <Box sx={{ p: 2 }}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No worries. Search again anytime.</Typography>
                </Paper>
              </Box>
            )}
            {!isGap && !searchError && answer && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ mt: 2 }}>
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Best Answer</Typography>
                    {/* Show summary view unless detailedView is active */}
                      {!detailedView && (
                      <>
                      <Box sx={{ p: 3 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.8,
                            mb: 2,
                            color: isGapMessage(answer) ? tokens.color.status.warnText : undefined
                          }}
                        >
                          {answer}
                        </Typography>

                        {matchedContent && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 3
                            }}
                          >
                            <StarBorderIcon
                              sx={{
                                fontSize: 16,
                                color: tokens.color.textTertiary
                              }}
                            />

                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {matchedContent.title}
                              {matchedContent.duration && ` (${matchedContent.duration}min)`}
                              {/* Removed 10 Oct 2026 (N4 pt 1): the page only; a video or audio passage now shows its time. */}
                              {seekLabel(matchedContent.seek) ? ` · ${seekLabel(matchedContent.seek)}` : ''}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                        
                        {!isNoTopicCover(answer) && (queryId || feedbackGiven) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <Typography variant="body2">Did this answer your question?</Typography>
                            <Button variant="contained" color="primary" size="small" disabled={!!feedbackGiven} onClick={() => sendFeedback('yes', matchedContent.id)}>Yes</Button>
                            <Button variant="outlined" color="primary" size="small" disabled={!!feedbackGiven} onClick={() => sendFeedback('no', matchedContent.id)}>No</Button>
                            {/* Skip next to Yes and No, as in the app (N4 pt 3): saves 'skip' and keeps the answer */}
                            {!feedbackGiven && (
                              <Box component="button" onClick={() => sendFeedback('skip', matchedContent.id, true)} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer', textDecoration: 'underline' }}>Skip</Box>
                            )}
                          </Box>
                        )}

                        {!isNoTopicCover(answer) && feedbackGiven === 'skip' && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No worries. Search again anytime.</Typography>
                        )}

                        {!isNoTopicCover(answer) && feedbackGiven === 'yes' && (
                          <Box sx={{ mt: 2, width: '100%', bgcolor: tokens.color.status.activeBg, color: tokens.color.status.activeText, p: 2, borderRadius: 1, textAlign: 'center' }}>
                            Glad that helped.
                          </Box>
                        )}

                        {!isNoTopicCover(answer) && feedbackGiven === 'no' && (
                          <Box sx={{ mt: 2, width: '100%', p: 2, background:tokens.color.bgTertiary }}>
                            <Box sx={{ mb: 1, fontWeight: 600 }}>What didn’t work?</Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button variant="contained" size="small" onClick={() => {
                                  // open detailed view: display the FULL answer (not just summary)
                                  setDetailedView(true);
                                  // Use fullAnswer which contains the complete, untruncated answer
                                  if (fullAnswer) {
                                    setAnswer(fullAnswer);
                                  }
                                  setShowFollowup(false);
                                  setFeedbackGiven(null);
                                  setViewingRelated(false);
                                }}>Need more details</Button>                                
                                <Button variant="outlined" size="small" onClick={() => {
                                  // call related modules flow
                                  fetchRelatedModules();
                                }}>Not what I meant</Button>
                              </Box>
                              <Box>
                                <Box component="button" onClick={() => {
                                  // Skip saves 'skip' and ends the flow; the No given just before stays on the search (N4 pt 3).
                                  // The answer stays with "No worries. Search again anytime.", as in the app (N4 C6); it used
                                  // to clear the widget. Removed 10 Oct 2026 (N4 C6): clearing the answer on this Skip.
                                  sendFeedback('skip', matchedContent.id, true);
                                  setShowFollowup(false);
                                }} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer' }}>Skip</Box>
                              </Box>
                            </Box>

                            {showFollowup && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">Sorry that wasn't right — try 'Need more details' to see a longer answer or 'Not what I meant' to refine.</Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </>
                    )}

                    {/* Detailed view: only show detailed answer + content link/video + resolved feedback */}
                    {detailedView && (
                      <Box sx={{ mt: 2 }}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 1, bgcolor: tokens.color.bgSecondary }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{answer}</Typography>
                          {!isNoTopicCover(answer) && detailedView && matchedContent && (
                            <Box sx={{ mt: 2 }}>
                              <ContentPlayer
                                contentId={matchedContent.id}
                                courseId={matchedContent.courseId}
                                seek={matchedContent.seek || null}
                                queryId={queryId}
                                isLearnerMode={true}
                                isSearchResult
                                isInline={true}
                                isModal={false}
                                hideCourseNavigation
                                showCloseButton
                                onClose={closePrimarySearchPlayer}
                              />
                              
                              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', p: 1, gap: 1,border: `1px solid ${tokens.color.borderSecondary}`, borderRadius: '8px', }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{matchedContent.title}</Typography>
                                {matchedContent.duration && (
                                  <Typography variant="body2" color="text.secondary">({matchedContent.duration} min)</Typography>
                                )}
                                {/* Removed 10 Oct 2026 (N4 pt 1): the page only; a video or audio passage now shows its time. */}
                                {seekLabel(matchedContent.seek) && (
                                  <Typography variant="body2" color="text.secondary">{seekLabel(matchedContent.seek)}</Typography>
                                )}
                                {matchedContent.courseName && (
                                  <Typography variant="body2" color="text.secondary">from {matchedContent.courseName}</Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {!isNoTopicCover(answer) && feedbackGiven === 'yes' && (
                            <Box sx={{ mt: 2, width: '100%', bgcolor: tokens.color.status.activeBg, color: tokens.color.status.activeText, p: 2, borderRadius: 1, textAlign: 'center' }}>
                              Glad that helped.
                            </Box>
                          )}

                          {/* Skip here ends the flow with "No worries. Search again anytime." under the answer, as on the
                              short view (N4 C6). Removed 11 Oct 2026 (N4 C6): keeping the Resolved? row after Skip
                              with nothing to say the Skip was taken. */}
                          {!isNoTopicCover(answer) && feedbackGiven === 'skip' && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No worries. Search again anytime.</Typography>
                          )}

                          {!isNoTopicCover(answer) && feedbackGiven !== 'skip' && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="body2">Resolved?</Typography>
                              <Button size="small" variant="contained" disabled={feedbackGiven === 'yes'} onClick={() => resolveQuery('yes')}>Yes</Button>
                              <Button size="small" variant="outlined" disabled={feedbackGiven === 'no'} onClick={() => resolveQuery('no')}>No</Button>
                              <Box sx={{ marginLeft: 'auto' }}>
                                {/* The answer stays with "No worries", as in the app (N4 C6). Removed 10 Oct 2026 (N4 C6): clearing it. */}
                                <Box component="button" onClick={() => { sendFeedback('skip', matchedContent.id, true); }} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer' }}>Skip</Box>
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            )}

            {/* Related modules progressive flow */}
            {viewingRelated && relatedModules && relatedModules.length > 0 && (
              (() => {
                const batchSize = 2;
                const start = relatedBatchIndex * batchSize;
                const batch = relatedModules.slice(start, start + batchSize);
                const totalBatches = Math.ceil(relatedModules.length / batchSize);
                const titles = [
                  'You might be looking for',
                  'A couple more options',
                  'Last suggestions — or try rephrasing above'
                ];
                const title = titles[Math.min(relatedBatchIndex, titles.length - 1)];

                const handleNo = () => {
                  const next = relatedBatchIndex + 1;
                    if (next >= totalBatches) {
                    // final: None of These behavior — clear UI and show gap message
                    // ask server to record 'skip' but preserve local gap UI
                    sendFeedback('skip', null, true);
                    setViewingRelated(false);
                    setRelatedModules([]);
                    // clear answer/details and ids, and show gap
                    setFullAnswer(null);
                    setAnswer(null);
                    setIsGap(true);
                    setGapQueryId(queryId);
                    setQueryId(null);
                    setMatchedContent(null);
                    setTopMatches([]);
                  } else {
                    setRelatedBatchIndex(next);
                  }
                };

                // Skip on the related list ends the flow with "No worries. Search again anytime.", as in the app (N4 C6).
                // Removed 10 Oct 2026 (N4 C6): ending on a blank widget (the answer is already cleared for the list).
                const handleSkip = () => {
                  sendFeedback('skip', null, true);
                  setViewingRelated(false);
                  setRelatedSkipped(true);
                };

                return (
                  <Box sx={{ p: 2 }}>
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backgroundColor: isGapMessage(answer) ? tokens.color.status.warnBg : undefined, border: isGapMessage(answer) ? `0.5px solid ${tokens.color.status.warnText}` : undefined }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: isGapMessage(answer) ? tokens.color.status.warnText : undefined }}>{title}</Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {batch.map(m => (
                          <Box
                            key={m.id}
                            onClick={() => handleRelatedModuleClick(m)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border: `1px solid ${tokens.color.borderSecondary}`,
                              borderRadius: '16px',
                              px: 2,
                              py: 1.75,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              width: '100%',
                              '&:hover': {
                                borderColor: tokens.color.borderTertiary,
                                backgroundColor: '#FAFAFA'
                              }
                            }}
                          >
                            {/* Left Side */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '10px',
                                  backgroundColor: tokens.color.bgTertiary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <AppsIcon
                                  sx={{
                                    fontSize: 18,
                                    color: tokens.color.textTertiary
                                  }}
                                />
                              </Box>

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: '18px',
                                    fontWeight: 500,
                                    color: tokens.color.textPrimary,
                                    lineHeight: 1.2
                                  }}
                                >
                                  {m.title}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {m.duration ? `${m.duration} min` : ''}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Right Arrow */}
                            <Typography
                              sx={{
                                color: tokens.color.textTertiary,
                                fontSize: '24px',
                                lineHeight: 1
                              }}
                            >
                              ›
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                          {/* Left action: No or None of These on final batch */}
                          <Button variant="contained" onClick={handleNo}>
                            {relatedBatchIndex + 1 >= Math.ceil(relatedModules.length / 2) ? 'None of These' : 'No'}
                          </Button>

                          <Box sx={{ marginLeft: 'auto' }}>
                            <Box component="button" onClick={handleSkip} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer' }}>Skip</Box>
                          </Box>
                        </Box>
                    </Paper>
                  </Box>
                );
              })()
            )}

            {relatedLoading && <ThinkingIndicator />}

            {/* A related pick with no answer (N4 pt 1): the gap message, and the way on to the other related items */}
            {isViewingSecondaryAnswer && secondaryIsGap && (
              <Box sx={{ p: 2 }}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backgroundColor: tokens.color.status.warnBg, border: `0.5px solid ${tokens.color.status.warnText}` }}>
                  <ContentSearchMessage kind="gap" sx={{ fontSize: '16px', color: tokens.color.status.warnText }} />
                  <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" size="small" onClick={showNextRelatedOrGap}>Not what I meant</Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* A related step failed (K15 pt 5): the error state, without leaving the conversation */}
            {relatedFailure && (
              <Box sx={{ p: 2 }}>
                <ContentSearchMessage kind="error" onRetry={retryRelatedStep} />
              </Box>
            )}

            {/* Secondary answer section (from clicking related module) */}
            {isViewingSecondaryAnswer && !secondaryIsGap && secondaryAnswer && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ mt: 2 }}>
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Best Answer</Typography>
                    
                    {!secondaryDetailedView && (
                      <>
                        <Box sx={{ p: 3 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              lineHeight: 1.8,
                              mb: 2
                            }}
                          >
                            {secondaryAnswer}
                          </Typography>

                          {secondaryMatchedContent && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                              <StarBorderIcon sx={{ fontSize: 16, color: tokens.color.textTertiary }} />
                              <Typography variant="body2" color="text.secondary">
                                {secondaryMatchedContent.title}
                                {secondaryMatchedContent.duration && ` (${secondaryMatchedContent.duration}min)`}
                                {/* Removed 10 Oct 2026 (N4 pt 1): the page only; a video or audio passage now shows its time. */}
                                {seekLabel(secondaryMatchedContent.seek) ? ` · ${seekLabel(secondaryMatchedContent.seek)}` : ''}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven !== 'yes' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <Typography variant="body2">Did this answer your question?</Typography>
                            <Button variant="contained" color="primary" size="small" disabled={!!secondaryFeedbackGiven} onClick={() => sendSecondaryFeedback('yes', secondaryMatchedContent?.id)}>Yes</Button>
                            <Button variant="outlined" color="primary" size="small" disabled={!!secondaryFeedbackGiven} onClick={() => sendSecondaryFeedback('no', secondaryMatchedContent?.id)}>No</Button>
                            {!secondaryFeedbackGiven && (
                              <Box component="button" onClick={() => sendSecondaryFeedback('skip', secondaryMatchedContent?.id, true)} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer', textDecoration: 'underline' }}>Skip</Box>
                            )}
                          </Box>
                        )}

                        {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven === 'skip' && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No worries. Search again anytime.</Typography>
                        )}

                        {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven === 'yes' && (
                          <Box sx={{ mt: 2, width: '100%', bgcolor: tokens.color.status.activeBg, color: tokens.color.status.activeText, p: 2, borderRadius: 1, textAlign: 'center' }}>
                            Glad that helped.
                          </Box>
                        )}

                        {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven === 'no' && (
                          <Box sx={{ mt: 2, width: '100%', p: 2, background:tokens.color.bgTertiary }}>
                            <Box sx={{ mb: 1, fontWeight: 600 }}>What didn't work?</Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button variant="contained" size="small" onClick={() => {
                                  setSecondaryDetailedView(true);
                                  if (secondaryFullAnswer) setSecondaryAnswer(secondaryFullAnswer);
                                  setSecondaryFeedbackGiven(null);
                                }}>Need more details</Button>
                                <Button variant="outlined" size="small" onClick={showNextRelatedOrGap}>Not what I meant</Button>
                              </Box>
                              <Box>
                                {/* Skip keeps the related answer and says "No worries. Search again anytime." under it, as
                                    for the first answer (N4 C6). Removed 11 Oct 2026 (N4 C6): clearing the related answer,
                                    which left the widget blank (the first answer is already cleared for the list). */}
                                <Box component="button" onClick={() => sendSecondaryFeedback('skip', secondaryMatchedContent?.id, true)} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer' }}>Skip</Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}

                    {secondaryDetailedView && (
                      <Box sx={{ mt: 2 }}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 1, bgcolor: '#fafafa' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{secondaryAnswer}</Typography>

                          {!isNoTopicCover(secondaryAnswer) && secondaryMatchedContent && (
                            <Box sx={{ mt: 2 }}>
                              <ContentPlayer
                                contentId={secondaryMatchedContent.id}
                                courseId={secondaryMatchedContent.courseId}
                                seek={secondaryMatchedContent.seek || null}
                                queryId={queryId}
                                isLearnerMode={true}
                                isSearchResult
                                isInline={true}
                                isModal={false}
                                hideCourseNavigation
                                showCloseButton
                                onClose={closePrimarySearchPlayer}
                              />

                              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{secondaryMatchedContent.title}</Typography>
                                {secondaryMatchedContent.duration && (
                                  <Typography variant="body2" color="text.secondary">({secondaryMatchedContent.duration} min)</Typography>
                                )}
                                {/* Removed 10 Oct 2026 (N4 pt 1): the page only; a video or audio passage now shows its time. */}
                                {seekLabel(secondaryMatchedContent.seek) && (
                                  <Typography variant="body2" color="text.secondary">{seekLabel(secondaryMatchedContent.seek)}</Typography>
                                )}
                                {secondaryMatchedContent.courseName && (
                                  <Typography variant="body2" color="text.secondary">from {secondaryMatchedContent.courseName}</Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven === 'yes' && (
                            <Box sx={{ mt: 2, width: '100%', bgcolor: tokens.color.status.activeBg, color: tokens.color.status.activeText, p: 2, borderRadius: 1, textAlign: 'center' }}>
                              Glad that helped.
                            </Box>
                          )}

                          {/* Skip here keeps the related answer and ends with "No worries. Search again anytime." (N4 C6).
                              Removed 11 Oct 2026 (N4 C6): clearing the related answer, which left the widget blank. */}
                          {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven === 'skip' && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No worries. Search again anytime.</Typography>
                          )}

                          {!isNoTopicCover(secondaryAnswer) && secondaryFeedbackGiven !== 'skip' && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="body2">Resolved?</Typography>
                              <Button size="small" variant="contained" disabled={secondaryFeedbackGiven === 'yes'} onClick={() => resolveSecondaryQuery('yes')}>Yes</Button>
                              <Button size="small" variant="outlined" disabled={secondaryFeedbackGiven === 'no'} onClick={() => resolveSecondaryQuery('no')}>No</Button>
                              <Box sx={{ marginLeft: 'auto' }}>
                                <Box component="button" onClick={() => sendSecondaryFeedback('skip', secondaryMatchedContent?.id, true)} sx={{ background: 'transparent', border: 'none', color: tokens.color.brand, cursor: 'pointer' }}>Skip</Box>
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            )}

            {/* Learning Center Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 3,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: tokens.color.bgSecondary
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    backgroundColor: tokens.color.brandLight,
                    borderRadius: 2,
                    p: 1,
                    mr: 2
                  }}
                >
                  <AppsIcon color="primary" />
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: tokens.color.brand
                  }}
                >
                  Learning Center
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Courses, guides, videos
              </Typography>
            </Box>

            <Divider />

            <Box
              sx={{
                textAlign: 'center',
                py: 1,
                fontSize: '12px',
                color: tokens.color.textTertiary
              }}
            >
              Powered by Your LMS
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default EmbeddedDashboard;